import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack, Surface } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Card,
  Dialog,
  EmptyState,
  StatusChip,
  Table,
  Tabs,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { formatInZone, formatMoney, PAYMENT_LABEL, paymentTone } from "../format";
import { formatLabel } from "../labels";
import type { BookingDto, BookingOverview } from "../types";

const METHOD_LABEL: Record<string, string> = {
  qr: "QR scan",
  manual: "Code typed by staff",
  staff: "Staff",
};

/**
 * Everything an operator needs about one booking, read from real rows: who and
 * where, the money (ledger and refunds), attendance with every scan attempt, the
 * group it came from, and one timeline of what happened.
 */
export function BookingOverviewPanel({
  booking,
  overview,
  onResetTicket,
  resetBusy,
  resetError,
}: {
  booking: BookingDto;
  overview: BookingOverview;
  onResetTicket: () => Promise<boolean>;
  resetBusy: boolean;
  resetError: string | null;
}) {
  const navigate = useNavigate();
  const [confirmReset, setConfirmReset] = useState(false);
  const zone = booking.booking_timezone;
  const { payment, attendance, group } = overview;
  const ticketLive = !attendance.checked_in_at && (booking.status === "confirmed" || booking.status === "in_progress");

  const field = (label: string, value: string) => (
    <Flex justify="space-between" gap={12}>
      <Text role="small" color="secondary" as="span">
        {label}
      </Text>
      <Text role="body" as="span">
        {value}
      </Text>
    </Flex>
  );

  return (
    <Stack gap={16}>
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        <Card header={<Text role="title" as="h3">Who and where</Text>}>
          <Stack gap={8}>
            {field("Player", overview.customer.name ?? "No display name")}
            {field("Venue", overview.business.name ?? "—")}
            {field("Court", overview.resource?.name ?? "—")}
            {field("Listing", overview.offer?.name ?? "—")}
            {field("Starts", formatInZone(booking.starts_at, zone))}
            {field("Ends", formatInZone(booking.ends_at, zone))}
            {field("Time zone", zone)}
            {field("Booked", formatInZone(overview.created_at, zone))}
            <Flex gap={8} wrap>
              <Button size="sm" variant="secondary" onClick={() => void navigate(`/app/users/${overview.customer.id}`)}>
                Open player
              </Button>
            </Flex>
          </Stack>
        </Card>

        <Card header={<Text role="title" as="h3">Money</Text>}>
          <Stack gap={8}>
            {field("Price at booking", formatMoney(overview.price.amount, overview.price.currency))}
            <Flex justify="space-between" gap={12}>
              <Text role="small" color="secondary" as="span">
                Payment
              </Text>
              <StatusChip tone={paymentTone(payment.status)}>{PAYMENT_LABEL[payment.status]}</StatusChip>
            </Flex>
            {field("Paid online", formatMoney(payment.paid_amount, payment.currency ?? booking.currency))}
            {field("Refunded", formatMoney(payment.refunded_amount, payment.currency ?? booking.currency))}
            {payment.status === "none" ? (
              <Text role="caption" color="muted" as="p">
                No online payment is recorded. The player may have paid at the venue; HUDDL cannot see that.
              </Text>
            ) : null}
            {payment.refunds.length > 0 ? (
              <Text role="caption" color="muted" as="p">
                {payment.refunds.length} refund record{payment.refunds.length === 1 ? "" : "s"}; worked in Finance.
              </Text>
            ) : null}
          </Stack>
        </Card>

        <Card header={<Text role="title" as="h3">Attendance</Text>}>
          <Stack gap={8}>
            {attendance.checked_in_at ? (
              <>
                <StatusChip tone="success">Checked in</StatusChip>
                {field("At", formatInZone(attendance.checked_in_at, zone))}
                {field("How", METHOD_LABEL[attendance.checked_in_method ?? ""] ?? "—")}
                {field("By", attendance.checked_in_by?.name ?? "—")}
              </>
            ) : attendance.no_show_at ? (
              <>
                <StatusChip tone="danger">No-show</StatusChip>
                {field("Recorded", formatInZone(attendance.no_show_at, zone))}
                {field("By", attendance.no_show_marked_by?.name ?? "—")}
              </>
            ) : (
              <Text role="body" color="secondary" as="p">
                Not checked in. The venue checks players in by scanning the ticket QR in the Business Manager.
              </Text>
            )}
            {field("Scan attempts", String(attendance.scans.length))}
            {ticketLive ? (
              <Stack gap={4}>
                <Button size="sm" variant="secondary" disabled={resetBusy} onClick={() => setConfirmReset(true)}>
                  Reset ticket QR
                </Button>
                <Text role="caption" color="muted" as="p">
                  Use when a player says their ticket was shared. Every QR shown so far stops working.
                </Text>
              </Stack>
            ) : null}
            {resetError ? (
              <Alert tone="danger" title="Could not reset the QR">
                {resetError}
              </Alert>
            ) : null}
          </Stack>
        </Card>
      </div>

      <Surface>
        <Tabs
          items={[
            {
              id: "timeline",
              label: `Timeline (${overview.timeline.length})`,
              content:
                overview.timeline.length === 0 ? (
                  <EmptyState title="No history recorded" description="Status changes, payments and scans appear here as they happen." />
                ) : (
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>When</Th>
                        <Th>What</Th>
                        <Th>By</Th>
                        <Th>Detail</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {overview.timeline.map((e, i) => (
                        <Tr key={`${e.at}-${i}`}>
                          <Td>{formatInZone(e.at, zone)}</Td>
                          <Td>{e.label}</Td>
                          <Td>{e.actor_name ?? "—"}</Td>
                          <Td>{e.note ?? "—"}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ),
            },
            {
              id: "scans",
              label: `Check-in scans (${attendance.scans.length})`,
              content:
                attendance.scans.length === 0 ? (
                  <EmptyState title="No scans yet" description="Every scan at the venue, accepted or refused, is recorded here." />
                ) : (
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>When</Th>
                        <Th>Result</Th>
                        <Th>Method</Th>
                        <Th>Staff</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {attendance.scans.map((s) => (
                        <Tr key={s.id}>
                          <Td>{formatInZone(s.at, zone)}</Td>
                          <Td>
                            <StatusChip tone={s.outcome === "checked_in" ? "success" : s.outcome === "already_checked_in" ? "info" : "warning"}>
                              {s.label}
                            </StatusChip>
                          </Td>
                          <Td>{METHOD_LABEL[s.method] ?? formatLabel(s.method)}</Td>
                          <Td>{s.actor_name ?? "—"}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ),
            },
            {
              id: "payments",
              label: `Payments (${payment.transactions.length + payment.refunds.length})`,
              content:
                payment.transactions.length === 0 && payment.refunds.length === 0 ? (
                  <EmptyState title="No ledger entries" description="Online payments and refunds for this booking appear here." />
                ) : (
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>When</Th>
                        <Th>Entry</Th>
                        <Th>Status</Th>
                        <Th>Amount</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {payment.transactions.map((t) => (
                        <Tr key={t.id}>
                          <Td>{formatInZone(t.occurred_at, zone)}</Td>
                          <Td>{formatLabel(t.type)}</Td>
                          <Td>{formatLabel(t.status)}</Td>
                          <Td>{formatMoney(t.amount, t.currency)}</Td>
                        </Tr>
                      ))}
                      {payment.refunds.map((r) => (
                        <Tr key={r.id}>
                          <Td>{formatInZone(r.issued_at ?? r.requested_at, zone)}</Td>
                          <Td>Refund{r.reason_type ? ` · ${formatLabel(r.reason_type)}` : ""}</Td>
                          <Td>{formatLabel(r.status)}</Td>
                          <Td>{formatMoney(r.amount, payment.currency ?? booking.currency)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ),
            },
            {
              id: "group",
              label: "Group",
              content: !group ? (
                <EmptyState title="Not a group booking" description="This booking was made by one player." />
              ) : (
                <Stack gap={8}>
                  {field("Group", group.name)}
                  {field("Organizer", group.organizer.name ?? "—")}
                  {field("Split", group.split_mode === "equal" ? "Equal split" : formatLabel(group.split_mode))}
                  {field("Group status", formatLabel(group.status))}
                  {field("Max members", String(group.max_members))}
                </Stack>
              ),
            },
          ]}
        />
      </Surface>

      <Dialog
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        title="Reset this ticket's QR?"
        actions={
          <Flex gap={8} justify="flex-end">
            <Button variant="secondary" onClick={() => setConfirmReset(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              disabled={resetBusy}
              onClick={async () => {
                if (await onResetTicket()) setConfirmReset(false);
              }}
            >
              Reset QR
            </Button>
          </Flex>
        }
      >
        <Text role="body" as="p">
          Every QR code shown for {booking.display_code ?? "this booking"} so far stops working at once. The player's app
          shows a new one the next time they open the ticket. The booking itself is not changed.
        </Text>
      </Dialog>
    </Stack>
  );
}
